import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero, GameIcon } from "../../../components/game";

export default function AppCoachSettings() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Profile form
  const [profileForm, setProfileForm] = useState({
    full_name: "",
    phone: "",
    locale: "ro",
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({
        full_name: user.full_name || "",
        phone: (user as any).phone || "",
        locale: user.locale || "ro",
      });
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await api.patch(`/users/${user?.id}`, {
        full_name: profileForm.full_name || null,
        phone: profileForm.phone || null,
        locale: profileForm.locale,
      });

      // Change language if needed
      if (profileForm.locale !== i18n.language) {
        i18n.changeLanguage(profileForm.locale);
        localStorage.setItem("locale", profileForm.locale);
      }

      await refresh();
      setSuccess(t("settings.profileSaved", "Profilul a fost salvat!"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      setError(err.response?.data?.error || t("settings.saveError", "Nu s-a putut salva profilul"));
    }
    setSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t("settings.passwordMismatch", "Parolele nu coincid"));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t("settings.passwordTooShort", "Parola trebuie să aibă minim 6 caractere"));
      return;
    }

    setChangingPassword(true);
    try {
      await api.post("/users/me/change-password", {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      setPasswordSuccess(t("settings.passwordChanged", "Parola a fost schimbată!"));
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err: any) {
      console.error("Failed to change password:", err);
      setPasswordError(err.response?.data?.error || t("settings.passwordError", "Nu s-a putut schimba parola"));
    }
    setChangingPassword(false);
  };

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <GameHero className="rounded-3xl px-6 py-7 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-white/15 border-4 border-white/40 flex items-center justify-center text-white text-2xl font-extrabold shadow-lg">
            {user?.full_name?.charAt(0) || "A"}
          </div>
          <h1 className="text-xl font-extrabold text-white mt-4">{t("settings.title", "Setări")}</h1>
          <p className="text-white/80 text-sm">{user?.email}</p>
        </GameHero>

        {/* Profile Form */}
        <form onSubmit={handleSaveProfile} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>
          )}
          {success && (
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </div>
          )}

          {/* Personal Info Card */}
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
              <h2 className="font-extrabold text-slate-800">{t("settings.personalInfo", "Informații personale")}</h2>
            </div>
            <div className="p-4 space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("settings.fullName", "Nume complet")}
                </label>
                <input
                  type="text"
                  value={profileForm.full_name}
                  onChange={e => setProfileForm({ ...profileForm, full_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t("settings.fullNamePlaceholder", "ex: Ion Popescu")}
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("settings.phone", "Telefon")}
                </label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t("settings.phonePlaceholder", "ex: 0722 123 456")}
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("settings.email", "Email")}
                </label>
                <div className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500">
                  {user?.email}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {t("settings.emailHint", "Email-ul nu poate fi modificat")}
                </p>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("settings.language", "Limba")}
                </label>
                <select
                  value={profileForm.locale}
                  onChange={e => setProfileForm({ ...profileForm, locale: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="ro">Română</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </GameCard>

          {/* Save Button */}
          <GameButton type="submit" variant="mint" disabled={saving}>
            {saving ? t("common.saving", "Se salvează...") : t("settings.saveChanges", "Salvează modificările")}
          </GameButton>
        </form>

        {/* Security Section */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h2 className="font-extrabold text-slate-800">{t("settings.security", "Securitate")}</h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Change Password */}
            {!showPasswordForm ? (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 active:translate-y-[2px] transition-transform"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <GameIcon name="lock" className="w-5 h-5" />
                  </div>
                  <span className="font-bold text-slate-700">{t("settings.changePassword", "Schimbă parola")}</span>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{passwordError}</div>
                )}
                {passwordSuccess && (
                  <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {passwordSuccess}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("settings.currentPassword", "Parola actuală")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("settings.newPassword", "Parola nouă")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("settings.confirmPassword", "Confirmă parola nouă")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    minLength={6}
                  />
                </div>

                <div className="flex gap-3">
                  <GameButton
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                      setPasswordError("");
                    }}
                  >
                    {t("common.cancel", "Anulează")}
                  </GameButton>
                  <GameButton type="submit" variant="primary" disabled={changingPassword}>
                    {changingPassword ? t("common.saving", "Se salvează...") : t("settings.save", "Salvează")}
                  </GameButton>
                </div>
              </form>
            )}
          </div>
        </GameCard>

        {/* Danger Zone */}
        <GameCard className="overflow-hidden border-red-100">
          <div className="px-4 py-3 bg-red-50 border-b border-red-100">
            <h2 className="font-extrabold text-red-800">{t("settings.dangerZone", "Zona periculoasă")}</h2>
          </div>
          <div className="p-4">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center justify-between p-3 bg-red-50 rounded-xl hover:bg-red-100 active:translate-y-[2px] transition-transform"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <span className="font-bold text-red-700">{t("settings.deleteAccount", "Șterge contul")}</span>
                </div>
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-xl">
                  <p className="text-sm text-red-700">
                    {t("settings.deleteWarning", "Pentru a șterge contul, contactează administratorul clubului sau trimite un email la support@igrowth.ro")}
                  </p>
                </div>
                <GameButton variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                  {t("common.understood", "Am înțeles")}
                </GameButton>
              </div>
            )}
          </div>
        </GameCard>

        {/* App Version */}
        <div className="text-center text-slate-400 text-xs py-4">
          <p>iGrowth v1.0.0</p>
        </div>
      </div>
    </MobileAppLayout>
  );
}
